import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { Wrapper } from "@googlemaps/react-wrapper";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import PlacesAutocomplete from "../search/PlacesAutocomplete";
import { useAppSelector } from "../../app/hooks";
import { selectActiveTenantId } from "../activeTenant/activeTenantSlice";
import { selectUserInfo } from "../userInfo/userInfoSlice";
import {
  createExclusionZone,
  deleteExclusionZone,
  getExclusionZoneApiErrorMessage,
  getExclusionZones,
  setExclusionZoneActive,
  updateExclusionZone,
  type ExclusionZoneDTO,
} from "./exclusionZoneApi";

const SETTINGS_RESOURCE = "vision_data4home-settings";

function hasSettingsScope(userInfo: any, scope: string) {
  return userInfo?.rtp?.some((item: any) => item.resource === SETTINGS_RESOURCE && item.scope?.includes(scope));
}

const API_KEY = "AIzaSyDq2gssPa5aiQDHXNsQU7fP9DZd-v5pzho";

type SelectedPlace = { placeId: string; label: string };

type FormState = {
  editingId: number | null;
  name: string;
  start: SelectedPlace | null;
  end: SelectedPlace | null;
  active: boolean;
};

const EMPTY_FORM: FormState = { editingId: null, name: "", start: null, end: null, active: true };

function toSelectedPlace(option: any): SelectedPlace | null {
  const placeId = option?.value?.place_id;
  return placeId ? { placeId, label: option.label ?? option.value.description ?? "" } : null;
}

export default function ExclusionZones() {
  const { enqueueSnackbar } = useSnackbar();
  const userInfo = useAppSelector(selectUserInfo);
  const tenants = userInfo.value?.tenants ?? [];
  const tenantId = useAppSelector(selectActiveTenantId) ?? tenants[0]?.tenantId ?? "";
  const canEdit = hasSettingsScope(userInfo.value, "edit");
  const canDelete = hasSettingsScope(userInfo.value, "delete");

  const [zones, setZones] = useState<ExclusionZoneDTO[]>([]);
  const [loadingZones, setLoadingZones] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  // Remonte les champs d'adresse (état interne) quand on change de zone éditée ou qu'on réinitialise
  const [formKey, setFormKey] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [zoneToDelete, setZoneToDelete] = useState<ExclusionZoneDTO | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const loadZones = useCallback(async () => {
    if (!tenantId) return;
    setLoadingZones(true);
    setLoadError(null);
    try {
      setZones(await getExclusionZones(tenantId));
    } catch (error) {
      setLoadError(getExclusionZoneApiErrorMessage(error, "Impossible de charger les zones d'exclusion."));
    } finally {
      setLoadingZones(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setFormKey((k) => k + 1);
  };

  useEffect(() => {
    resetForm();
  }, [tenantId]);

  const handleEdit = (zone: ExclusionZoneDTO) => {
    setForm({
      editingId: zone.id,
      name: zone.name,
      start: { placeId: zone.startPlaceId, label: zone.startAddress },
      end: { placeId: zone.endPlaceId, label: zone.endAddress },
      active: zone.active,
    });
    setFormError(null);
    setFormKey((k) => k + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (form.name.trim() === "") {
      setFormError("Le nom de la zone est obligatoire.");
      return;
    }
    if (!form.start || !form.end) {
      setFormError("Sélectionnez une adresse de début et une adresse de fin dans les suggestions.");
      return;
    }

    const payload = {
      tenantId,
      name: form.name.trim(),
      startPlaceId: form.start.placeId,
      endPlaceId: form.end.placeId,
      active: form.active,
    };

    setSaving(true);
    setFormError(null);
    try {
      if (form.editingId != null) {
        await updateExclusionZone(form.editingId, payload);
        enqueueSnackbar("Zone d'exclusion modifiée", { variant: "success" });
      } else {
        await createExclusionZone(payload);
        enqueueSnackbar("Zone d'exclusion créée", { variant: "success" });
      }
      resetForm();
      await loadZones();
    } catch (error) {
      setFormError(getExclusionZoneApiErrorMessage(error, "L'enregistrement de la zone a échoué."));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (zone: ExclusionZoneDTO) => {
    setTogglingId(zone.id);
    try {
      const updated = await setExclusionZoneActive(zone.id, !zone.active, tenantId);
      setZones((current) => current.map((z) => (z.id === updated.id ? updated : z)));
    } catch (error) {
      enqueueSnackbar(getExclusionZoneApiErrorMessage(error, "La mise à jour de la zone a échoué."), { variant: "error" });
    } finally {
      setTogglingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!zoneToDelete) return;
    const zone = zoneToDelete;
    setZoneToDelete(null);
    try {
      await deleteExclusionZone(zone.id, tenantId);
      setZones((current) => current.filter((z) => z.id !== zone.id));
      if (form.editingId === zone.id) {
        resetForm();
      }
      enqueueSnackbar("Zone d'exclusion supprimée", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(getExclusionZoneApiErrorMessage(error, "La suppression de la zone a échoué."), { variant: "error" });
    }
  };

  if (!userInfo.value) {
    return <CircularProgress />;
  }
  if (tenants.length === 0) {
    return (
      <Alert severity="warning">
        Aucun groupe client n'est associé à votre compte. Contactez l'administrateur.
      </Alert>
    );
  }
  const isEditing = form.editingId != null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography variant="body2" color="text.secondary">
        Les adresses comprises dans une zone active restent consultables par les utilisateurs de votre groupe,
        mais sont signalées comme exclues des zones de souscription et l'export PDF est désactivé.
        Les numéros de début et de fin sont inclus, des deux côtés de la rue.
      </Typography>

      {/* Formulaire de création / modification */}
      <Paper variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {isEditing ? "Modifier la zone" : "Nouvelle zone d'exclusion"}
        </Typography>

        <TextField
          label="Nom de la zone"
          size="small"
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
          inputProps={{ maxLength: 120 }}
          required
        />

        <Wrapper apiKey={API_KEY} libraries={["places", "marker", "geometry"]}>
          <Box key={formKey} sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 280 }}>
              <Typography variant="caption" color="text.secondary">Adresse de début *</Typography>
              <PlacesAutocomplete
                initialValue={form.start?.label}
                onChange={(option: any) => setForm((f) => ({ ...f, start: toSelectedPlace(option) }))}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 280 }}>
              <Typography variant="caption" color="text.secondary">Adresse de fin *</Typography>
              <PlacesAutocomplete
                initialValue={form.end?.label}
                onChange={(option: any) => setForm((f) => ({ ...f, end: toSelectedPlace(option) }))}
              />
            </Box>
          </Box>
        </Wrapper>

        <FormControlLabel
          control={<Switch checked={form.active} disabled={!canEdit} onChange={(event) => setForm({ ...form, active: event.target.checked })} />}
          label={form.active ? "Active" : "Inactive"}
        />

        {formError && <Alert severity="error">{formError}</Alert>}

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" onClick={handleSubmit} disabled={saving || !canEdit}>
            {saving ? <CircularProgress size={20} /> : isEditing ? "Enregistrer" : "Ajouter la zone"}
          </Button>
          {isEditing && (
            <Button variant="text" onClick={resetForm} disabled={saving}>Annuler</Button>
          )}
        </Box>
      </Paper>

      {/* Liste des zones */}
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Zones d'exclusion ({zones.length})
        </Typography>
        {loadError && <Alert severity="error" sx={{ mb: 1 }}>{loadError}</Alert>}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Rue</TableCell>
                <TableCell>Numéros</TableCell>
                <TableCell>Début</TableCell>
                <TableCell>Fin</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingZones && (
                <TableRow>
                  <TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell>
                </TableRow>
              )}
              {!loadingZones && zones.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">Aucune zone d'exclusion.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loadingZones && zones.map((zone) => (
                <TableRow key={zone.id} selected={form.editingId === zone.id} sx={{ opacity: zone.active ? 1 : 0.6 }}>
                  <TableCell sx={{ fontWeight: 500 }}>{zone.name}</TableCell>
                  <TableCell>{zone.streetLabel}, {zone.postalCode} {zone.locality ?? ""}</TableCell>
                  <TableCell>{zone.startNumber} → {zone.endNumber}</TableCell>
                  <TableCell>{zone.startAddress}</TableCell>
                  <TableCell>{zone.endAddress}</TableCell>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={zone.active}
                      disabled={!canEdit || togglingId === zone.id}
                      onChange={() => handleToggleActive(zone)}
                      inputProps={{ "aria-label": zone.active ? "Désactiver la zone" : "Activer la zone" }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title={canEdit ? "Modifier" : "Permission de modification requise"}>
                      <span>
                        <IconButton size="small" disabled={!canEdit} onClick={() => handleEdit(zone)}><EditIcon fontSize="small" /></IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={canDelete ? "Supprimer" : "Permission de suppression requise"}>
                      <span>
                        <IconButton size="small" disabled={!canDelete} onClick={() => setZoneToDelete(zone)}><DeleteIcon fontSize="small" /></IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={zoneToDelete != null} onClose={() => setZoneToDelete(null)}>
        <DialogTitle>Supprimer la zone d'exclusion ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            La zone « {zoneToDelete?.name} » sera définitivement supprimée. Les adresses concernées
            redeviendront éligibles à la souscription.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZoneToDelete(null)}>Annuler</Button>
          <Button color="error" onClick={handleConfirmDelete}>Supprimer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
